import { useState } from 'react';
import { motion } from 'framer-motion';
import { FiPlus } from 'react-icons/fi';
import AdminTables from '@components/admin/AdminTables';
import AdminForms from '@components/admin/AdminForms';
import Modal from '@components/common/Modal';
import Button from '@components/common/Button';
import { useProducts } from '@hooks/useProducts';
import toast from 'react-hot-toast';

const AdminProducts = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  const { products, loading, createProduct, updateProduct, deleteProduct } = useProducts({ admin: true });

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'sku', label: 'SKU' },
    { key: 'title', label: 'Name' },
    { 
      key: 'price', 
      label: 'Price',
      render: (value) => `£${value.toFixed(2)}`
    },
    { key: 'stock', label: 'Stock' },
    {
      key: 'stockStatus',
      label: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
          value === 'in-stock' ? 'bg-green-100 text-green-600' :
          value === 'low-stock' ? 'bg-orange-100 text-orange-600' :
          'bg-red-100 text-red-600'
        }`}>
          {value === 'in-stock' ? 'In Stock' :
           value === 'low-stock' ? 'Low Stock' :
           'Out of Stock'}
        </span>
      )
    },
  ];

  const formFields = [
    { name: 'title', label: 'Product Name', type: 'text', required: true },
    { name: 'sku', label: 'SKU', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'textarea', required: true },
    { 
      name: 'price', 
      label: 'Price', 
      type: 'number', 
      required: true,
      step: 0.01,
      validation: { min: 0 }
    },
    { 
      name: 'stock', 
      label: 'Stock Quantity', 
      type: 'number', 
      required: true,
      validation: { min: 0 }
    },
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      required: true,
      options: [
        { value: 'cameras', label: 'Security Cameras' },
        { value: 'alarms', label: 'Alarm Systems' },
        { value: 'access-control', label: 'Access Control' },
        { value: 'smart-locks', label: 'Smart Locks' },
      ]
    },
    {
      name: 'stockStatus',
      label: 'Stock Status',
      type: 'select',
      required: true,
      options: [
        { value: 'in-stock', label: 'In Stock' },
        { value: 'low-stock', label: 'Low Stock' },
        { value: 'out-of-stock', label: 'Out of Stock' },
      ]
    },
    { name: 'image', label: 'Image URL', type: 'text', required: true },
  ];

  const handleAdd = async (data) => {
    try {
      await createProduct(data);
      toast.success('Product added successfully!');
      setIsAddModalOpen(false);
    } catch (error) {
      toast.error('Failed to add product');
    }
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setIsEditModalOpen(true);
  };

  const handleUpdate = async (data) => {
    try {
      await updateProduct(selectedProduct.id, data);
      toast.success('Product updated successfully!');
      setIsEditModalOpen(false);
      setSelectedProduct(null);
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const handleDelete = async (product) => {
    if (window.confirm(`Are you sure you want to delete "${product.title}"?`)) {
      try {
        await deleteProduct(product.id);
        toast.success('Product deleted successfully!');
      } catch (error) {
        toast.error('Failed to delete product');
      }
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-600 mt-2">Manage your product catalog</p>
        </div>
        <Button
          text="Add Product"
          type="primary"
          onClick={() => setIsAddModalOpen(true)}
          icon={<FiPlus />}
        />
      </div>

      <AdminTables
        columns={columns}
        data={products}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isLoading={loading}
      />

      {/* Add Product Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Product"
        size="lg"
      >
        <AdminForms
          fields={formFields}
          onSubmit={handleAdd}
          submitButtonText="Add Product"
        />
      </Modal>

      {/* Edit Product Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedProduct(null);
        }}
        title="Edit Product"
        size="lg"
      >
        {selectedProduct && (
          <AdminForms
            fields={formFields}
            initialData={selectedProduct}
            onSubmit={handleUpdate}
            submitButtonText="Update Product"
          />
        )}
      </Modal>
    </div>
  );
};

export default AdminProducts;

